import { useQuery } from '@tanstack/vue-query'
import { apiClient } from '@/apis/instances/api-client'
import { checkApiResponse } from '@/apis/instances/check-api-response'
import { ApiV2DepartmentElFindParams } from '@/apis/swaggers/v2/v2-swagger'
import { QUERY_KEY } from '@/apis/swaggers/swagger-docs'
import { QueryComposableParams } from '@/types/query-options'
import { resolveMaybeGetter } from '@/util/resolve-maybe-getter'

type QueryParams = QueryComposableParams<ApiV2DepartmentElFindParams, unknown>

const fetchDepartmentList = async (params: ApiV2DepartmentElFindParams) => {
  const { data } = await apiClient.v2.apiV2DepartmentElFind(params);
  return checkApiResponse(data);
}

export const v2DepartmentKeys = {
  all: [QUERY_KEY.v2apiV2DepartmentElFind] as const,
  list: (params: ApiV2DepartmentElFindParams) => [...v2DepartmentKeys.all, params] as const,
}

/** 명령형(await) 호출용 쿼리 정의 — 컴포넌트 밖(컨트롤러 등)에서는
 * `import { queryClient } from '@/apis/instances/query-client'` 로 클라이언트를 가져와
 * `queryClient.fetchQuery(v2DepartmentFindQueryOptions(params))` 로 사용한다 */
export const v2DepartmentFindQueryOptions = (params: ApiV2DepartmentElFindParams) => ({
  queryKey: v2DepartmentKeys.list(params),
  queryFn: () => fetchDepartmentList(params),
})

export const useV2DepartmentFindQuery = ({ params, options }: QueryParams) => {
  return useQuery(() => ({
    queryKey: v2DepartmentKeys.list(resolveMaybeGetter(params)),
    queryFn: () => fetchDepartmentList(resolveMaybeGetter(params)),
    ...resolveMaybeGetter(options ?? {}),
  }))
}

/**
 * 부서 목록 표준 조회 — 정렬 포함 표준 파라미터로 캐시 키를 단일화한다. FCM 푸시 미커버 도메인이라 staleTime 5분.
 * 특수 조건 조회(기간 필터·keyword 등)는 기존 v2DepartmentFindQueryOptions 를 그대로 사용한다 (staleTime 0 유지).
 */
export const v2DepartmentListQueryOptions = (hospitalID: string) => ({
  // swagger 생성 타입이 hospitalID 를 boolean 으로 잘못 문서화하고 있어 캐스팅 (서버 스웨거 주석 오류)
  ...v2DepartmentFindQueryOptions({ hospitalID, isRemoved: false, key: 'sortNum', way: 1 } as unknown as ApiV2DepartmentElFindParams),
  staleTime: 5 * 60 * 1000,
  gcTime: 5 * 60 * 1000,
})
